import React, { useState } from 'react';
//@ts-ignore
import * as styles from './PromotionalDiscountOverlay.module.css';
import loadable from '@loadable/component';
import CtaLink from '../Global/CtaLink';

const JotForm = loadable(() => import('../Jotform'));
import RichText from '../Global/RichText';
import { graphql } from 'gatsby';
import Modal from 'react-modal';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import {
    getMediaData,
    getColor,
    getMultiChoice,
    optimizeKontentImageUrl,
    imagePreset,
    buildKontentSrcSet,
} from '../Global/DataUtils/index';
import CloseButton from '../Global/CloseButton';

import {
    KontentStringValue,
    KontentMedia,
    BrandColor,
    KontentRichText,
    CTAConfiguration,
} from '../../../types/KontentProps';

interface Props extends CTAConfiguration {
    content_panel__content_panel_color: BrandColor;
    content_panel__content_panel_font_color: BrandColor;
    discount: {
        value: {
            elements: discountElements;
        }[];
    };
    display_image: {
        value: [
            {
                name: string;
                codename: string;
            }
        ];
    };

    image: KontentMedia;
    kontentItemId?: string;
}

interface discountElements extends CTAConfiguration {
    activation_date: {
        value: KontentStringValue;
    };
    expiration_date: {
        value: KontentStringValue;
    };

    discount_start: {
        value: KontentStringValue;
    };
    discount_end: {
        value: KontentStringValue;
    };

    copy: KontentRichText;
    cta_type: {
        value: [
            {
                name: string;
                codename: string;
            }
        ];
    };

    headline: {
        value: KontentStringValue;
    };

    form_selection: {
        value: string;
    };
    display_type: {
        value: [
            {
                name: string;
                codename: string;
            }
        ];
    };
}

interface countDown {
    days?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
    difference: number;
}

const PromotionalDiscountOverlay: React.FC<Props> = ({
    content_panel__content_panel_font_color,
    content_panel__content_panel_color,
    image,
    discount,
    display_image,
    kontentItemId,
}) => {
    const countType =
        discount?.value[0]?.elements.display_type?.value[0]?.codename;
    const count_type = countType ? countType.toLowerCase() : '';
    const calculateTimeLeft = () => {
        let targetDate =
            count_type == 'count_down'
                ? new Date(
                      discount?.value[0]?.elements?.discount_end?.value.toString()
                  )
                : new Date(
                      discount?.value[0]?.elements?.discount_start.value.toString()
                  );

        const difference = +targetDate - +new Date();
        let timeLeft: countDown = { difference: 0 };

        if (difference > 0) {
            timeLeft = {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60),
                difference,
            };
        } else {
            timeLeft = { difference: 0 };
        }
        // if time is above expiration and below activation donot show
        return timeLeft;
    };

    const [timeLeft, setTimeLeft] = useState<countDown>({ difference: 0 });
    const [displayDiscountBanner, setdisplayDiscountBanner] = useState(false);
    const countDownHeader = ['days', 'hours', 'minutes', 'seconds'];
    let isImage = display_image?.value[0]?.codename == 'yes' ? true : false;
    const [isActive, setActive] = useState(true);
    const headlineValue =
        countType == 'count_down' ? 'promotion ends in' : 'promotion begins in';
    const discountBgImageLarge = getMediaData(image)?.url;
    const [mobileView, setMobileView] = React.useState<Boolean>();
    const backgroundColor = getColor(
        content_panel__content_panel_color
    )?.toLowerCase();
    const textColor = getColor(content_panel__content_panel_font_color);
    const discountBgImageLargeAltText = 'sunlightenImage';

    const ctaLocation = discount?.value[0]?.elements
        ?.cta_configuration__cta_location_anchor_point?.value
        ? BuildLinkHref(
              discount?.value[0]?.elements?.cta_configuration__cta_location
          ) +
          '#' +
          discount?.value[0]?.elements
              ?.cta_configuration__cta_location_anchor_point.value
        : BuildLinkHref(
              discount?.value[0]?.elements?.cta_configuration__cta_location
          );

    const cta = {
        label: discount?.value[0]?.elements?.cta_configuration__cta_label
            ?.value,
        location: ctaLocation,
        target:
            getMultiChoice(
                discount?.value[0]?.elements
                    ?.cta_configuration__cta_window_action
            ) === 'new'
                ? '_blank'
                : '_self',
    };
    const [showForm, setShowForm] = useState(false);

    const handleForm = (e: React.MouseEvent<HTMLButtonElement>) => {
        setShowForm(true);
    };

    const handleFormDone = () => {
        setShowForm(false);
    };

    const checkDiscountBanner = () => {
        const todaysDate = new Date();
        const activateDate = new Date(
            discount?.value[0]?.elements?.activation_date.value.toString()
        );
        const expirationDate = new Date(
            discount?.value[0]?.elements?.expiration_date.value.toString()
        );

        if (todaysDate >= activateDate && todaysDate <= expirationDate) {
            setdisplayDiscountBanner(true);
        } else {
            setdisplayDiscountBanner(false);
        }
    };

    React.useEffect(() => {
        if (timeLeft.difference > 0) {
            setTimeout(() => {
                setTimeLeft(calculateTimeLeft());
            }, 1000);
        }
    }, [timeLeft]);

    const handleResize = () => {
        if (window.innerWidth < 770) {
            setMobileView(true);
        } else {
            setMobileView(false);
        }
    };

    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        handleResize();
        checkDiscountBanner();
        let targetDate =
            count_type == 'count_down'
                ? new Date(
                      discount?.value[0]?.elements?.discount_end?.value?.toString()
                  )
                : new Date(
                      discount?.value[0]?.elements?.discount_start?.value?.toString()
                  );
        const difference = +targetDate - +new Date();
        setTimeLeft({ difference });
    }, [
        discount?.value[0]?.elements?.discount_start,
        discount?.value[0]?.elements?.discount_end,
    ]);

    const closeModal = () => {
        setActive(false);
    };

    const customStylesMobile = {
        content: {
            inset: '10%',
            overflow: 'auto',
            padding: '0',
            height: 'fit-content',
        },
    };
    const customStylesDesktop = {
        content: {
            inset: '8%',
            overflow: mobileView ? 'auto' : 'null',
            height: 'fit-content',
            padding: '0',
            background: 'none',
            border: 'none',
        },
    };
    const BackgroundColor = backgroundColor ? backgroundColor : '';
    const TextColor = textColor ? textColor : '';

    const handleCloseBtnClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        setActive(false);
    };
    return (
        <Modal
            contentLabel="Promotion"
            isOpen={displayDiscountBanner ? isActive : false}
            onRequestClose={closeModal}
            style={mobileView ? customStylesMobile : customStylesDesktop}
            overlayClassName={styles.overlay}
        >
            <div
                className={styles.componentWrapper}
                data-kontent-item-id={kontentItemId}
            >
                {isImage && (
                    <div className={styles.imageWrapper}>
                        {isImage ? (
                            <picture
                                className={styles.picture}
                                data-kontent-element-codename="display_image"
                            >
                                {discountBgImageLarge && (
                                    <img
                                        src={optimizeKontentImageUrl(
                                            discountBgImageLarge,
                                            {
                                                ...imagePreset.fullWidth
                                                    .desktop,
                                                q: 95,
                                            }
                                        )}
                                        srcSet={buildKontentSrcSet(
                                            discountBgImageLarge,
                                            imagePreset.fullWidth.desktop.w,
                                            { fm: 'webp' as const, q: 95 }
                                        )}
                                        sizes="100vw"
                                        alt={discountBgImageLargeAltText}
                                    />
                                )}
                            </picture>
                        ) : null}
                    </div>
                )}

                <div
                    style={{
                        backgroundColor: BackgroundColor,
                        color: TextColor,
                    }}
                    className={`${styles.contentWrappers} ${
                        !isImage ? styles.noimagecontentWrappers : null
                    }`}
                >
                    <div className={styles.closeButton}>
                        <CloseButton
                            visualStyle="light"
                            onClick={handleCloseBtnClick}
                        />
                    </div>
                    {displayDiscountBanner ? (
                        <div
                            style={{ display: 'flex' }}
                            className={styles.wrapperContainer}
                        >
                            <div
                                className={`${styles.timerBanner}  ${
                                    isImage ? styles.withImage : null
                                } ${
                                    !isImage ? styles.noimagetimerBanner : null
                                } `}
                            >
                                <div
                                    className={styles.promotionBanner}
                                    data-kontent-element-codename="headline"
                                >
                                    {discount?.value[0]?.elements?.headline
                                        ?.value
                                        ? discount?.value[0]?.elements?.headline
                                              .value
                                        : headlineValue}
                                </div>

                                <div
                                    className={`${
                                        styles.countdownTimeContainer
                                    } ${!isImage}`}
                                >
                                    {Object.keys(timeLeft).length > 1
                                        ? Object.keys(timeLeft).map(
                                              (key: string, index: number) => {
                                                  if (
                                                      key != 'difference' &&
                                                      (key == 'days' ||
                                                          key == 'hours' ||
                                                          key == 'minutes' ||
                                                          key == 'seconds')
                                                  ) {
                                                      return (
                                                          <div
                                                              className={
                                                                  styles.countdown
                                                              }
                                                              key={index}
                                                          >
                                                              <span
                                                                  className={
                                                                      styles.countDownNumber
                                                                  }
                                                              >
                                                                  {
                                                                      timeLeft[
                                                                          key
                                                                      ]
                                                                  }
                                                              </span>
                                                              <span
                                                                  className={
                                                                      styles.countDownText
                                                                  }
                                                              >
                                                                  {key.toUpperCase()}
                                                              </span>
                                                          </div>
                                                      );
                                                  }
                                              }
                                          )
                                        : countDownHeader.map(
                                              (key: String, index: number) => {
                                                  return (
                                                      <div
                                                          className={
                                                              styles.countdown
                                                          }
                                                          key={index}
                                                      >
                                                          <span
                                                              className={
                                                                  styles.countDownNumber
                                                              }
                                                          >
                                                              00
                                                          </span>
                                                          <span
                                                              className={
                                                                  styles.countDownText
                                                              }
                                                          >
                                                              {key.toUpperCase()}
                                                          </span>
                                                      </div>
                                                  );
                                              }
                                          )}
                                </div>
                            </div>
                            <div className={styles.container}>
                                <div
                                    className={styles.promotionalHeading}
                                    style={{
                                        textAlign: !isImage
                                            ? 'center'
                                            : 'match-parent',
                                    }}
                                >
                                    {RichText(
                                        discount?.value[0]?.elements?.copy,
                                        undefined,
                                        undefined,
                                        undefined,
                                        'copy'
                                    )}
                                </div>
                            </div>
                            <div
                                style={{ marginTop: '3vw' }}
                                className={`${styles.buttons} ${
                                    styles.timerBanner
                                }  ${!isImage ? styles.noimagebutton : null}`}
                            >
                                {discount?.value[0]?.elements?.cta_type
                                    ?.value[0]?.name == 'Page' ||
                                discount?.value[0]?.elements?.cta_type?.value[0]
                                    ?.name == 'page' ? (
                                    <div
                                        className={styles.customCTAButton}
                                        style={{
                                            color: TextColor,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <CtaLink
                                            href={cta.location}
                                            target={cta.target}
                                            color={TextColor}
                                            hoverBorderColor={TextColor}
                                            hoverFontColor={TextColor}
                                            hoverBgColor={BackgroundColor}
                                        >
                                            {cta.label}
                                        </CtaLink>
                                    </div>
                                ) : (
                                    <div
                                        className={styles.customCTAButton}
                                        style={{ textAlign: 'center' }}
                                    >
                                        <button
                                            onClick={handleForm}
                                            type="button"
                                            style={{
                                                color: TextColor,
                                                border: `2px solid ${TextColor}`,
                                                backgroundColor:
                                                    BackgroundColor,
                                            }}
                                        >
                                            {cta.label}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {showForm && (
                                <div className={styles.jotFormStyle}>
                                    <JotForm
                                        formStyle="popup"
                                        formUrl={
                                            discount?.value[0]?.elements
                                                ?.form_selection?.value
                                        }
                                        display={showForm}
                                        onPopUpClose={handleFormDone}
                                    />
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
        </Modal>
    );
};

export default PromotionalDiscountOverlay;

export const fragmentPromotionalDiscountOverlay = graphql`
    fragment PromotionalDiscountOverlay on kontent_item_component___promotion_discount_overlay {
        elements {
            content_panel__content_panel_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                            color_name {
                                value
                            }
                        }
                    }
                }
            }
            content_panel__content_panel_font_color {
                value {
                    ... on kontent_item_list___brand_colors {
                        id
                        elements {
                            color_hex_value {
                                value
                            }
                            color_name {
                                value
                            }
                        }
                    }
                }
            }
            discount {
                value {
                    ... on kontent_item_content_item___discount {
                        id
                        elements {
                            activation_date {
                                value(
                                    difference: ""
                                    formatString: ""
                                    fromNow: false
                                    locale: ""
                                )
                            }
                            copy {
                                value
                            }
                            cta_configuration__cta_label {
                                value
                            }
                            cta_configuration__cta_location {
                                value {
                                    ... on kontent_item_content_item___third_party_link {
                                        id
                                        elements {
                                            third_party_url {
                                                value
                                            }
                                        }
                                    }
                                    ... on kontent_item_layout___master_page {
                                        id
                                        elements {
                                            slug {
                                                value
                                            }
                                            display_page_name {
                                                value
                                            }
                                            page_search_summary {
                                                value
                                            }
                                        }
                                    }
                                }
                            }
                            cta_configuration__cta_location_anchor_point {
                                value
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    codename
                                }
                            }
                            cta_type {
                                value {
                                    codename
                                    name
                                }
                            }
                            discount_end {
                                value(
                                    difference: ""
                                    formatString: ""
                                    fromNow: false
                                    locale: ""
                                )
                            }
                            discount_start {
                                value(
                                    difference: ""
                                    formatString: ""
                                    fromNow: false
                                    locale: ""
                                )
                            }
                            display_type {
                                value {
                                    codename
                                    name
                                }
                            }
                            expiration_date {
                                value(
                                    difference: ""
                                    formatString: ""
                                    fromNow: false
                                    locale: ""
                                )
                            }
                            form_selection {
                                value
                            }
                            headline {
                                value
                            }
                        }
                    }
                }
            }
            display_image {
                value {
                    codename
                }
            }
            image {
                value {
                    ... on kontent_item_media___image {
                        id
                        elements {
                            file {
                                value {
                                    url
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;
